import React, { useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';

const CHECK_EVERY_MS = 5 * 60 * 1000;
const AUTO_RELOAD_S = 10;

// Nome do bundle principal que esta aba carregou (ex.: main.a549552b.js)
const mainBundle = (html) => (html.match(/\/static\/js\/(main\.[a-z0-9]+\.js)/) || [])[1] || null;
const loadedBundle = () => {
  const el = [...document.scripts].find((s) => /\/static\/js\/main\.[a-z0-9]+\.js/.test(s.src));
  return el ? (el.src.match(/(main\.[a-z0-9]+\.js)/) || [])[1] : null;
};

const isTyping = () => {
  const a = document.activeElement;
  return !!a && (a.tagName === 'INPUT' || a.tagName === 'TEXTAREA' || a.tagName === 'SELECT' || a.isContentEditable);
};

/**
 * Detecta quando o servidor publicou uma versão nova e atualiza a aba sozinha,
 * sem precisar de Ctrl+F5. Aba escondida recarrega na hora; aba em uso mostra um aviso
 * e recarrega em alguns segundos (ou espera, se a pessoa estiver digitando).
 */
export default function UpdateChecker() {
  const [pending, setPending] = useState(false);
  const [left, setLeft] = useState(AUTO_RELOAD_S);
  const busy = useRef(false);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return undefined;
    const current = loadedBundle();
    if (!current) return undefined;

    const check = async () => {
      if (busy.current) return;
      busy.current = true;
      try {
        const res = await fetch(`/index.html?_=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const latest = mainBundle(await res.text());
        if (latest && latest !== current) {
          if (document.visibilityState === 'hidden') window.location.reload();
          else setPending(true);
        }
      } catch { /* offline: tenta de novo depois */ } finally { busy.current = false; }
    };

    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    const timer = setInterval(check, CHECK_EVERY_MS);
    const first = setTimeout(check, 3000);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', check);
    return () => {
      clearInterval(timer); clearTimeout(first);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', check);
    };
  }, []);

  useEffect(() => {
    if (!pending) return undefined;
    setLeft(AUTO_RELOAD_S);
    const t = setInterval(() => {
      setLeft((n) => {
        if (n <= 1) {
          if (isTyping()) return 1; // não interrompe quem está digitando
          window.location.reload();
          return 0;
        }
        return n - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [pending]);

  if (!pending) return null;
  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[200] w-[calc(100%-2rem)] max-w-sm" role="status" data-testid="update-banner">
      <div className="flex items-center gap-3 rounded-2xl bg-slate-900 text-white shadow-2xl px-4 py-3">
        <RefreshCw size={18} className="text-emerald-400 flex-shrink-0 animate-spin" style={{ animationDuration: '2s' }} />
        <p className="text-sm flex-1 leading-tight"><strong>Nova versão do Gastify disponível</strong><br /><span className="text-slate-300 text-xs">Atualizando em {left}s…</span></p>
        <button onClick={() => window.location.reload()} className="text-xs font-bold bg-emerald-500 hover:bg-emerald-400 text-slate-900 rounded-xl px-3 py-2" data-testid="update-now">Atualizar</button>
      </div>
    </div>
  );
}
